<div class="w-full">
  <!-- Error / blocked device -->
  <div *ngIf="errorMessage"
    class="flex items-center gap-3 border border-alert_red_text_Color/30 rounded-xl bg-alert_red_bg_Color px-4 py-3 text-alert_red_text_Color poppinsMedium mb-5">
    <i class="bx bx-error-circle text-lg relative top-[1px]"></i>
    <p class="text-xs xl:text-sm leading-relaxed">{{ errorMessage }}</p>
  </div>

  <!-- Retry button (shown only when the camera/mic could not start) -->
  <div *ngIf="!testing && errorMessage" class="flex justify-center mb-2">
    <button type="button" (click)="startTest()"
      class="inline-flex items-center justify-center gap-2 min-w-[150px] bg-primary_Bg_Button_Color text-primary_Bg_Btn_Text_color poppinsBold text-base rounded-xl px-6 py-3 transition-all hover:opacity-90 active:scale-[0.98]">
      <i class="bx bx-refresh text-lg"></i>
      Try again
    </button>
  </div>

  <!-- Live test area — Figma content card.
       Rendered during `initializing` too (with a connecting overlay on the
       video) so the card opens at its final size and never resizes. -->
  <div *ngIf="initializing || testing"
    class="box-border flex flex-col gap-4 w-full max-w-[1040px] mx-auto border border-slate_200 rounded-xl shadow-panel p-4">

    <!-- status-panel: Camera / Microphone / Internet Speed -->
    <div class="flex items-stretch gap-2.5 w-full">
      <!-- Camera -->
      <div class="flex-1 min-w-0 flex items-center gap-2 p-2.5 rounded-xl border border-slate_200"
        [ngClass]="{
          'bg-slate_50': initializing,
          'bg-status_success_card_bg': !initializing && testing,
          'bg-status_error_card_bg': !initializing && !testing
        }">
        <div
          class="flex items-center justify-center shrink-0 w-9 h-9 rounded-full border border-slate_200 bg-admin_tab_border_colour">
          <i class="bx bx-video text-lg text-blue_Color"></i>
        </div>
        <div class="flex flex-col flex-1 min-w-0">
          <span
            class="poppinsSemiBold text-[13px] leading-tight tracking-[0.01em] text-dark_blue">Camera</span>
          <span class="poppinsMedium text-xs leading-tight"
            [ngClass]="{
              'text-secondary_Light_Text_Color': initializing,
              'text-Active_Otp_Border': !initializing && testing,
              'text-medium_red_color': !initializing && !testing
            }">
            {{ initializing ? 'Checking…' : testing ? 'Working properly' : 'Not working' }}
          </span>
        </div>
        <span *ngIf="!initializing"
          class="flex items-center justify-center shrink-0 w-[18px] h-[18px] rounded-full"
          [ngClass]="testing ? 'bg-Active_Otp_Border' : 'bg-status_error_cross'">
          <i class="bx text-[11px] text-white" [ngClass]="testing ? 'bx-check' : 'bx-x'"></i>
        </span>
      </div>

      <!-- Microphone -->
      <div class="flex-1 min-w-0 flex items-center gap-2 p-2.5 rounded-xl border border-slate_200"
        [ngClass]="{
          'bg-slate_50': micStatus === 'checking' || micStatus === 'waiting',
          'bg-status_success_card_bg': micStatus === 'detected',
          'bg-status_error_card_bg': micStatus === 'error'
        }">
        <div
          class="flex items-center justify-center shrink-0 w-9 h-9 rounded-full border border-slate_200 bg-admin_tab_border_colour">
          <i class="bx bx-microphone text-lg text-blue_Color"></i>
        </div>
        <div class="flex flex-col flex-1 min-w-0">
          <span
            class="poppinsSemiBold text-[13px] leading-tight tracking-[0.01em] text-dark_blue">Microphone</span>
          <span class="poppinsMedium text-xs leading-tight"
            [ngClass]="{
              'text-secondary_Light_Text_Color': micStatus === 'checking' || micStatus === 'waiting',
              'text-Active_Otp_Border': micStatus === 'detected',
              'text-medium_red_color': micStatus === 'error'
            }">
            {{ micStatus === 'checking' ? 'Checking…'
              : micStatus === 'waiting' ? 'Speak to test'
              : micStatus === 'detected' ? 'Working properly'
              : hasRecording ? 'No voice detected'
              : 'Not working' }}
          </span>
        </div>
        <!-- Detected: green check · Error: red cross · Waiting: pulsing "listening" dot -->
        <span *ngIf="micStatus === 'detected' || micStatus === 'error'"
          class="flex items-center justify-center shrink-0 w-[18px] h-[18px] rounded-full"
          [ngClass]="micStatus === 'detected' ? 'bg-Active_Otp_Border' : 'bg-status_error_cross'">
          <i class="bx text-[11px] text-white" [ngClass]="micStatus === 'detected' ? 'bx-check' : 'bx-x'"></i>
        </span>
        <span *ngIf="micStatus === 'waiting'"
          class="flex items-center justify-center shrink-0 w-[18px] h-[18px]">
          <span class="w-2.5 h-2.5 rounded-full bg-blue_Color animate-pulse"></span>
        </span>
      </div>

      <!-- Internet Speed -->
      <div class="flex-1 min-w-0 flex items-center gap-2 p-2.5 rounded-xl border border-slate_200"
        [ngClass]="{
          'bg-status_success_card_bg': networkQuality === 'good' || networkQuality === 'fair',
          'bg-status_error_card_bg': networkQuality === 'poor' || networkQuality === 'offline',
          'bg-slate_50': !networkQuality || networkChecking
        }">
        <div
          class="flex items-center justify-center shrink-0 w-9 h-9 rounded-full border border-slate_200 bg-slate_50">
          <i class="bx text-lg text-blue_Color"
            [ngClass]="networkQuality === 'poor' || networkQuality === 'offline' ? 'bx-wifi-off' : 'bx-wifi'"></i>
        </div>
        <div class="flex flex-col flex-1 min-w-0">
          <span
            class="poppinsSemiBold text-[13px] leading-tight tracking-[0.01em] text-dark_blue">Internet Speed</span>
          <span class="poppinsMedium text-xs leading-tight"
            [ngClass]="{
              'text-Active_Otp_Border': networkQuality === 'good' || networkQuality === 'fair',
              'text-medium_red_color': networkQuality === 'poor' || networkQuality === 'offline',
              'text-secondary_Light_Text_Color': !networkQuality || networkChecking
            }">
            <ng-container *ngIf="networkChecking">Checking…</ng-container>
            <ng-container *ngIf="!networkChecking">
              <span *ngIf="!networkOnline">Offline</span>
              <span *ngIf="networkOnline && networkDownlinkMbps != null">{{ networkDownlinkMbps }} Mbps</span>
              <span *ngIf="networkOnline && networkDownlinkMbps == null && networkLatencyMs != null">{{ networkLatencyMs }} ms</span>
              <span *ngIf="networkOnline && networkDownlinkMbps == null && networkLatencyMs == null">Online</span>
            </ng-container>
          </span>
        </div>
        <span *ngIf="!networkChecking && networkQuality"
          class="flex items-center justify-center shrink-0 w-[18px] h-[18px] rounded-full"
          [ngClass]="(networkQuality === 'good' || networkQuality === 'fair') ? 'bg-Active_Otp_Border' : 'bg-status_error_cross'">
          <i class="bx text-[11px] text-white"
            [ngClass]="(networkQuality === 'good' || networkQuality === 'fair') ? 'bx-check' : 'bx-x'"></i>
        </span>
      </div>
    </div>

    <!-- Video preview with overlaid live badge + vertical mic meter -->
    <div class="relative w-full rounded-xl border-[1.15px] border-slate_300 shadow-card overflow-hidden">
      <video #previewVideo autoplay playsinline
        class="block w-full aspect-video max-h-[500px] bg-slate_300 object-cover"></video>

      <!-- Connecting / waiting for permission (overlaid so the card keeps its size) -->
      <div *ngIf="initializing"
        class="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-slate_50">
        <span
          class="h-10 w-10 rounded-full border-[3px] border-gray-200 border-t-primary_Bg_Button_Color animate-spin"></span>
        <p class="poppinsMedium text-sm text-secondary_Light_Text_Color text-center">
          Connecting to your camera &amp; microphone…
          <br />
          <span class="text-xs">Please allow access when your browser asks.</span>
        </p>
      </div>

      <!-- Live / Recording badge -->
      <span *ngIf="testing && !hasRecording"
        class="absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 poppinsSemiBold text-xs text-white bg-black/40 backdrop-blur-sm">
        <span class="h-2 w-2 rounded-full" [ngClass]="recording ? 'bg-white animate-pulse' : 'bg-Active_Otp_Border'"></span>
        {{ recording ? 'RECORDING ' + recordCountdown + 's' : 'LIVE' }}
      </span>
      <span *ngIf="hasRecording"
        class="absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-md bg-black/40 px-2.5 py-1 poppinsSemiBold text-xs text-white backdrop-blur-sm">
        <i class="bx bx-play-circle text-sm"></i> PLAYBACK
      </span>

      <!-- Vertical mic level meter (overlaid on the right, near full height) -->
      <div *ngIf="testing"
        class="absolute top-1/2 -translate-y-1/2 right-3 h-[80%] flex flex-col items-center gap-2 rounded-xl bg-black/55 backdrop-blur-sm px-2 py-3 shadow-lg overflow-hidden">
        <i class="bx bx-volume-full text-lg text-white shrink-0"></i>
        <!-- Segments are stacked top → bottom, so reverse the index for level. -->
        <div class="flex flex-col justify-end gap-[5px] flex-1 min-h-0 w-5">
          <div *ngFor="let s of levelSegments; let i = index"
            class="w-full flex-1 min-h-0 rounded-[3px] transition-colors duration-75"
            [ngClass]="segmentClasses[i]"></div>
        </div>
        <i class="bx bx-microphone text-lg text-white shrink-0"></i>
      </div>
    </div>

    <!-- Playback result message -->


    <!-- footer-actions: message banner (left) + record controls (right) -->
    <div class="flex flex-col md:flex-row items-stretch gap-5 w-full">
      <!-- Left banner: swaps between info / warning / error / success -->
      <div class="flex-1 flex items-center gap-3 px-4 py-3 rounded-lg border border-slate_200"
        [ngClass]="{
          'bg-status_success_card_bg': hasRecording && voiceDetected,
          'bg-status_error_card_bg': hasRecording && !voiceDetected,
          'bg-info_banner_bg': !hasRecording
        }">
        <!-- Success: voice detected -->
        <ng-container *ngIf="hasRecording && voiceDetected">
          <i class="bx bx-check-circle text-xl text-Active_Otp_Border shrink-0"></i>
          <p class="poppinsRegular text-sm leading-[140%] text-Active_Otp_Border">
            Voice detected — play the clip above to confirm your audio is clear.
          </p>
        </ng-container>
        <!-- Warning: no voice detected -->
        <ng-container *ngIf="hasRecording && !voiceDetected">
          <i class="bx bx-error text-xl text-medium_red_color shrink-0"></i>
          <p class="poppinsRegular text-sm leading-[140%] text-medium_red_color">
            We couldn't detect your voice. Check your microphone and record
            again.
          </p>
        </ng-container>
        <!-- Recording hint: minimum length not yet met -->
        <ng-container *ngIf="!hasRecording && recording && !canStopRecording">
          <i class="bx bx-time-five text-xl text-header_admin_blue_color shrink-0"></i>
          <p class="poppinsRegular text-sm leading-[140%] text-info_banner_text">
            Please keep recording — a minimum of {{ minRecordSeconds }} seconds
            is required.
          </p>
        </ng-container>
        <!-- Default info -->
        <ng-container *ngIf="!hasRecording && !(recording && !canStopRecording)">
          <i class="bx bx-info-circle text-xl text-header_admin_blue_color shrink-0"></i>
          <p class="poppinsRegular text-sm leading-[140%] text-info_banner_text">
            You'll record a {{ recordSeconds }}-second clip. Make sure you're in
            a quiet environment.
          </p>
        </ng-container>
      </div>

      <!-- Record controls -->
      <!-- First recording -->
      <button type="button" *ngIf="!recording && !hasRecording" (click)="startRecording()"
        [disabled]="initializing"
        class="flex-1 inline-flex items-center justify-center gap-2.5 px-6 py-3.5 rounded-lg bg-primary_Bg_Button_Color text-primary_Bg_Btn_Text_color poppinsSemiBold text-base shadow-btn-yellow transition-all hover:opacity-90 active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none">
        <i class="bx bx-play text-lg"></i>
        Start Recording
      </button>

      <!-- Re-record after a clip exists -->
      <button type="button" *ngIf="!recording && hasRecording" (click)="startRecording()"
        class="flex-1 inline-flex items-center justify-center gap-2.5 px-6 py-3.5 rounded-lg bg-primary_Bg_Button_Color text-primary_Bg_Btn_Text_color poppinsSemiBold text-base shadow-btn-yellow transition-all hover:opacity-90 active:scale-[0.99]">
        <i class="bx bx-revision text-lg"></i>
        Record Again
      </button>

      <!-- Recording in progress — "Stop" is locked until the minimum length is met -->
      <button type="button" *ngIf="recording" (click)="stopRecording()"
        [disabled]="!canStopRecording"
        class="flex-1 inline-flex items-center justify-center gap-2.5 px-6 py-3.5 rounded-lg text-white poppinsSemiBold text-base shadow-btn-yellow"
        [ngClass]="canStopRecording
          ? 'bg-medium_red_color cursor-pointer'
          : 'bg-medium_red_color opacity-50 grayscale shadow-none cursor-not-allowed pointer-events-none'">
        <span class="relative flex h-3 w-3">
          <span class="absolute inline-flex h-full w-full rounded-full bg-white"></span>
          <span class="relative inline-flex h-3 w-3 rounded-full bg-white"></span>
        </span>
        <span class="inline-flex items-center gap-1.5">
          {{ canStopRecording ? 'Stop' : 'Recording' }}
        </span>
        <span class="ml-0.5 rounded-full text-sm">
          {{ recordCountdown + 's' }}
        </span>
      </button>
    </div>

    <!-- Listen-back consent (parent-driven; disabled until a clip exists). -->
    <div *ngIf="requireConsent" class="w-full">
      <label
        class="flex items-start gap-3 rounded-xl border px-4 py-3 transition-colors"
        [ngClass]="consentChecked ? 'border-slate_200 bg-status_success_card_bg' : 'border-slate_200 bg-slate_50'"
        [class.cursor-pointer]="hasRecording">
        <input type="checkbox" [checked]="consentChecked" [disabled]="!hasRecording"
          (change)="onConsentChange($any($event.target).checked)"
          class="mt-[3px] h-5 w-5 shrink-0 accent-primary_Bg_Button_Color cursor-pointer disabled:cursor-not-allowed disabled:opacity-60" />
        <span class="text-xs xl:text-sm poppinsMedium text-secondary_Light_Text_Color leading-relaxed">
          I have played back my recording and confirmed that my video is
          clearly visible and my audio is clear and audible. I am ready to
          proceed to the next step of the assessment.
        </span>
      </label>
    </div>
  </div>
</div>
